import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation, useParams } from 'react-router-dom'
import { RootState } from '../../../states/containers/store'
import {
  dateManipulateDay,
  endOfMonth,
  endOfWeek,
  format,
  nextMonth,
  nowDate,
  prevMonth,
  startOfMonth,
  startOfWeek,
} from '../../../utils/date'
import { getGetPrams } from '../../../utils/url'
import Holidays from 'date-holidays'
import { clone } from 'lodash'
import { LangValue } from '../../../states/types/common'

interface Props {
  date: string
  changeCallback: (date: string) => void
  omit?: boolean
  future?: boolean
  disabled: boolean
}

interface GetParams {
  date: string
}

const DateCalendarModule: FC<Props> = (props) => {
  const { t } = useTranslation()
  const location = useLocation()
  const { language } = useParams<{ language: LangValue }>()

  const {
    commonPart,
  } = useSelector((state: RootState) => state)

  const holidays = new Holidays()
  holidays.init('JP')

  const {
    date,
    changeCallback,
    omit,
    future,
    disabled,
  } = props

  const [startDate, setStartDate] = useState<string>()
  const [tempStartDate, setTempStartDate] = useState<string>()
  const [isCalendarDisplay, setIsCalendarDisplay] = useState(false)

  useEffect(() => {
    const getParams = getGetPrams(location.search) as GetParams
    if (!getParams.date) {
      setStartDate(date)
      setTempStartDate(date)
    } else {
      setStartDate(format(getParams.date))
      setTempStartDate(format(getParams.date))
    }
  }, [location, date])

  useEffect(() => {
    const html = document.querySelector('html')
    if (!html) return
    if (isCalendarDisplay) {
      html.classList.add('modal_open')
    } else {
      html.classList.remove('modal_open')
    }
  }, [isCalendarDisplay])

  const calendarColor = (day: string) => {
    if (Number.isNaN(new Date(day).getTime())) return "#666666"
    const holiday = holidays.isHoliday(new Date(day))
    if ((holiday && holiday[0].type === "public") || format(day, 'd') === '0') {
      return "#FF0000"
    }
    if (format(day, 'd') === '6') {
      return "#317AB7"
    }
    return "#666666"
  }

  const dayOfWeek = (day: string | undefined) => {
    if (!day || Number.isNaN(new Date(day).getTime())) return ""
    const holiday = holidays.isHoliday(new Date(day))
    let dd = format(day, "dd", language)
    if (holiday && holiday[0].type === "public") {
      dd = "祝"
    }
    return (
      <>
        (
        <span style={{ color: calendarColor(day) }}>
          {dd}
        </span>
        )
      </>
    )
  }

  const targetMonthData = useMemo(() => {
    const monthFirstWeekDay = startOfWeek(startOfMonth(tempStartDate || nowDate()))
    const monthLastWeekDay = endOfWeek(endOfMonth(tempStartDate || nowDate()))
    const dateList = []
    for (let d = clone(new Date(monthFirstWeekDay));d <= new Date(monthLastWeekDay);d.setDate(d.getDate() + 1)) {
      dateList.push(format(d, "YYYY-MM-DD"))
    }
    return Array.from(new Set(dateList))
  }, [tempStartDate])

  const handleChangeDate = (value: string) => {
    setIsCalendarDisplay(false)
    setStartDate(format(value))
    changeCallback(value)
  }

  return (
    <>
      <div className="calendar_text">
        <button
          type="button"
          className="display button"
          onClick={() => setIsCalendarDisplay(true)}
          disabled={disabled}
        >
          {format(startDate, "YYYY年M月D日", language)}
          &nbsp;
          {dayOfWeek(startDate)}
          <FontAwesomeIcon icon={['fas', 'angle-down']} className={`icon ${isCalendarDisplay && 'open'}`} />
        </button>
        {
          omit || (
            <>
              <button
                type="button"
                className="prev button"
                onClick={() => handleChangeDate(dateManipulateDay(startDate || nowDate(), -1, 'YYYY-MM-DD'))}
                disabled={
                  !commonPart.company?.created_at
                  || prevMonth(startDate || nowDate(), 'YYYY-MM-DD') === format(startOfMonth(commonPart.company?.created_at), 'YYYY-MM-DD')
                  || disabled
                }
              >
                <FontAwesomeIcon icon={['fas', 'angle-left']} />
              </button>
              <button
                type="button"
                className="next button"
                onClick={() => handleChangeDate(dateManipulateDay(startDate || nowDate(), 1, 'YYYY-MM-DD'))}
                disabled={
                  !commonPart.company?.created_at
                  || format(startDate, 'YYYY-MM-DD') === format(nowDate(), 'YYYY-MM-DD')
                  || disabled
                }
              >
                <FontAwesomeIcon icon={['fas', 'angle-right']} />
              </button>
            </>
          )
        }
      </div>
      <div className={`calendar_box ${isCalendarDisplay || 'none'}`}>
        <div className="range">
          <div className="text">
            {t(`range.calendar.header_text.daily`)}
          </div>
        </div>
        <div className="daily">
          <div className="select_month">
            <div>
              <button
                type="button"
                onClick={() => setTempStartDate(prevMonth(tempStartDate || nowDate(), "YYYY-MM-DD"))}
                disabled={Number(format(commonPart.company?.created_at, 'YYYYMM')) >= Number(format((tempStartDate || nowDate()), "YYYYMM"))}
              >
                <FontAwesomeIcon icon={['fas', 'angle-left']} />
              </button>
            </div>
            <div>
              {format(tempStartDate || nowDate(), "YYYY年MM月")}
            </div>
            <div>
              <button
                type="button"
                onClick={() => setTempStartDate(nextMonth(tempStartDate || nowDate(), "YYYY-MM-DD"))}
                disabled={(Number(nowDate("YYYYMM")) <= Number(format((tempStartDate || nowDate()), "YYYYMM")) && !future)}
              >
                <FontAwesomeIcon icon={['fas', 'angle-right']} />
              </button>
            </div>
          </div>
          <div className="date_wrap">
            {
              targetMonthData.map((v) => (
                <React.Fragment key={v}>
                  {
                    ((format(v, "MM") === format((tempStartDate || nowDate()), "MM")
                      && Number(format(v, "YYYYMMDD")) <= Number(nowDate("YYYYMMDD"))) || future) ? (
                        <div
                          // eslint-disable-next-line max-len
                          className={`date ${Number(format(v, "YYYYMMDD")) === Number(nowDate("YYYYMMDD")) ? "today" : ""} ${Number(format(v, "YYYYMMDD")) === Number(format(startDate, "YYYYMMDD")) ? "selected" : ""}`}
                          style={{
                            color: calendarColor(v),
                          }}
                          onClick={() => handleChangeDate(v)}
                          role="none"
                        >
                          {format(v, "D")}
                          <div className="bar" />
                        </div>
                      ) : (
                        <div
                          className="date disable"
                          style={{
                            color: calendarColor(v),
                          }}
                        >
                          {format(v, "D")}
                          <div className="bar" />
                        </div>
                      )
                  }
                </React.Fragment>
              ))
            }
          </div>
        </div>
      </div>
      <div className={`close_screen gray ${isCalendarDisplay || 'none'}`} role="presentation" onClick={() => setIsCalendarDisplay(false)} />
    </>
  )
}

DateCalendarModule.defaultProps = {
  omit: false,
  future: false,
}

export default DateCalendarModule
